import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, Alert, Linking } from 'react-native';
import {
  Text,
  List,
  Switch,
  Divider,
  Card,
  useTheme,
  Button,
  IconButton,
  Dialog,
  Portal,
  TextInput,
} from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { useAppDispatch, useAppSelector } from '../store';
import {
  fetchUserPreferences,
  updateUserPreferences,
  selectUserPreferences,
  selectUserUpdating,
} from '../store/slices/userSlice';
import {
  Shield,
  Eye,
  EyeOff,
  Lock,
  Users,
  Globe,
  AlertTriangle,
  Download,
  Trash2,
  Key,
  Bell,
  Mail,
  TrendingUp,
} from 'lucide-react-native';
import Toast from 'react-native-toast-message';
import { LoadingOverlay } from '../components/common/LoadingOverlay';
import { theme } from '../theme';

export function PrivacySecurityScreen() {
  const navigation = useNavigation();
  const dispatch = useAppDispatch();

  // User state
  const preferences = useAppSelector(selectUserPreferences);
  const isUpdating = useAppSelector(selectUserUpdating);

  // Local state
  const [privacyLevel, setPrivacyLevel] = useState<
    'public' | 'private' | 'friends'
  >('public');
  const [showProfile, setShowProfile] = useState(true);
  const [showRecipes, setShowRecipes] = useState(true);
  const [showStats, setShowStats] = useState(true);
  const [showActivity, setShowActivity] = useState(true);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(true);
  const [dataSharing, setDataSharing] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Load preferences on mount
  useEffect(() => {
    if (preferences) {
      setPrivacyLevel(preferences.privacy_level || 'public');
      setEmailNotifications(preferences.email_notifications || true);
      setPushNotifications(preferences.push_notifications || true);
    } else {
      dispatch(fetchUserPreferences());
    }
  }, [preferences]);

  const handlePrivacyChange = async (
    level: 'public' | 'private' | 'friends'
  ) => {
    try {
      setPrivacyLevel(level);
      await dispatch(updateUserPreferences({ privacy_level: level })).unwrap();
      Toast.show({
        type: 'success',
        text1: 'Privacy settings updated',
      });
    } catch (error) {
      Toast.show({
        type: 'error',
        text1: 'Failed to update privacy settings',
        text2: error as string,
      });
      // Revert on error
      setPrivacyLevel(preferences?.privacy_level || 'public');
    }
  };

  const handleNotificationChange = async (
    type: 'email' | 'push',
    value: boolean
  ) => {
    try {
      if (type === 'email') {
        setEmailNotifications(value);
        await dispatch(
          updateUserPreferences({ email_notifications: value })
        ).unwrap();
      } else {
        setPushNotifications(value);
        await dispatch(
          updateUserPreferences({ push_notifications: value })
        ).unwrap();
      }

      Toast.show({
        type: 'success',
        text1: 'Notification settings updated',
      });
    } catch (error) {
      Toast.show({
        type: 'error',
        text1: 'Failed to update notification settings',
        text2: error as string,
      });
      // Revert on error
      if (type === 'email') {
        setEmailNotifications(preferences?.email_notifications || true);
      } else {
        setPushNotifications(preferences?.push_notifications || true);
      }
    }
  };

  const handleDataExport = async () => {
    try {
      // This would call the API to export user data
      Toast.show({
        type: 'info',
        text1: 'Data export initiated',
        text2: 'You will receive an email when your data is ready',
      });
    } catch (error) {
      Toast.show({
        type: 'error',
        text1: 'Failed to initiate data export',
        text2: error as string,
      });
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      Toast.show({
        type: 'error',
        text1: 'Passwords do not match',
      });
      return;
    }

    if (newPassword.length < 8) {
      Toast.show({
        type: 'error',
        text1: 'Password must be at least 8 characters',
      });
      return;
    }

    try {
      // This would call the API to change password
      Toast.show({
        type: 'success',
        text1: 'Password changed successfully',
      });
      setShowPasswordDialog(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      Toast.show({
        type: 'error',
        text1: 'Failed to change password',
        text2: error as string,
      });
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword.trim()) {
      Toast.show({
        type: 'error',
        text1: 'Please enter your password',
      });
      return;
    }

    try {
      // This would call the API to delete account
      Toast.show({
        type: 'success',
        text1: 'Account deletion initiated',
        text2: 'You will receive a confirmation email',
      });
      setShowDeleteDialog(false);
      setDeletePassword('');
    } catch (error) {
      Toast.show({
        type: 'error',
        text1: 'Failed to delete account',
        text2: error as string,
      });
    }
  };

  const openPrivacyPolicy = () => {
    Linking.openURL('https://coffeeplexity.com/privacy');
  };

  const openTermsOfService = () => {
    Linking.openURL('https://coffeeplexity.com/terms');
  };

  const getPrivacyDescription = (level: 'public' | 'private' | 'friends') => {
    switch (level) {
      case 'public':
        return 'Your profile and recipes are visible to everyone';
      case 'friends':
        return 'Only your followers can see your full profile';
      case 'private':
        return 'Your profile is hidden from other users';
      default:
        return '';
    }
  };

  if (!preferences) {
    return <LoadingOverlay visible={true} />;
  }

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
    >
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Privacy Settings */}
        <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
          <Card.Content>
            <View style={styles.sectionHeader}>
              <Shield size={24} color={theme.colors.primary} />
              <Text variant='titleMedium' style={styles.sectionTitle}>
                Privacy Settings
              </Text>
            </View>

            <List.Item
              title='Profile Visibility'
              description={getPrivacyDescription(privacyLevel)}
              left={() => <Users size={24} color={theme.colors.outline} />}
            />
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              style={styles.privacySelectorContainer}
              contentContainerStyle={styles.privacySelector}
            >
              <Button
                mode={privacyLevel === 'public' ? 'contained' : 'outlined'}
                onPress={() => handlePrivacyChange('public')}
                compact
                style={styles.privacyButton}
              >
                Public
              </Button>
              <Button
                mode={privacyLevel === 'friends' ? 'contained' : 'outlined'}
                onPress={() => handlePrivacyChange('friends')}
                compact
                style={styles.privacyButton}
              >
                Friends
              </Button>
              <Button
                mode={privacyLevel === 'private' ? 'contained' : 'outlined'}
                onPress={() => handlePrivacyChange('private')}
                compact
                style={styles.privacyButton}
              >
                Private
              </Button>
            </ScrollView>
          </Card.Content>
        </Card>

        {/* Notification Settings */}
        <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
          <Card.Content>
            <View style={styles.sectionHeader}>
              <Bell size={24} color={theme.colors.primary} />
              <Text variant='titleMedium' style={styles.sectionTitle}>
                Notification Preferences
              </Text>
            </View>

            <List.Item
              title='Email Notifications'
              description='Receive notifications via email'
              left={() => <Mail size={24} color={theme.colors.outline} />}
              right={() => (
                <Switch
                  value={emailNotifications}
                  onValueChange={value =>
                    handleNotificationChange('email', value)
                  }
                  color={theme.colors.primary}
                  disabled={isUpdating}
                />
              )}
            />
            <Divider />
            <List.Item
              title='Push Notifications'
              description='Receive notifications on your device'
              left={() => <Bell size={24} color={theme.colors.outline} />}
              right={() => (
                <Switch
                  value={pushNotifications}
                  onValueChange={value =>
                    handleNotificationChange('push', value)
                  }
                  color={theme.colors.primary}
                  disabled={isUpdating}
                />
              )}
            />
          </Card.Content>
        </Card>

        {/* Data & Analytics */}
        <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
          <Card.Content>
            <View style={styles.sectionHeader}>
              <Globe size={24} color={theme.colors.primary} />
              <Text variant='titleMedium' style={styles.sectionTitle}>
                Data & Analytics
              </Text>
            </View>

            <List.Item
              title='Analytics & Insights'
              description='Help us improve by sharing anonymous usage data'
              left={() => <TrendingUp size={24} color={theme.colors.outline} />}
              right={() => (
                <Switch
                  value={analytics}
                  onValueChange={setAnalytics}
                  color={theme.colors.primary}
                />
              )}
            />
            <Divider />
            <List.Item
              title='Data Sharing'
              description='Share data with partners for better recommendations'
              left={() => <Users size={24} color={theme.colors.outline} />}
              right={() => (
                <Switch
                  value={dataSharing}
                  onValueChange={setDataSharing}
                  color={theme.colors.primary}
                />
              )}
            />
          </Card.Content>
        </Card>

        {/* Account Security */}
        <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
          <Card.Content>
            <View style={styles.sectionHeader}>
              <Lock size={24} color={theme.colors.primary} />
              <Text variant='titleMedium' style={styles.sectionTitle}>
                Account Security
              </Text>
            </View>

            <List.Item
              title='Change Password'
              description='Update your account password'
              left={() => <Key size={24} color={theme.colors.outline} />}
              onPress={() => setShowPasswordDialog(true)}
            />
            <Divider />
            <List.Item
              title='Two-Factor Authentication'
              description='Add an extra layer of security'
              left={() => <Shield size={24} color={theme.colors.outline} />}
              onPress={() => {
                Toast.show({
                  type: 'info',
                  text1: 'Coming Soon',
                  text2: 'Two-factor authentication will be available soon',
                });
              }}
            />
          </Card.Content>
        </Card>

        {/* Data Management */}
        <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
          <Card.Content>
            <View style={styles.sectionHeader}>
              <Download size={24} color={theme.colors.primary} />
              <Text variant='titleMedium' style={styles.sectionTitle}>
                Data Management
              </Text>
            </View>

            <List.Item
              title='Export My Data'
              description='Download a copy of all your data (GDPR compliant)'
              left={() => <Download size={24} color={theme.colors.outline} />}
              onPress={handleDataExport}
            />
            <Divider />
            <List.Item
              title='Delete Account'
              description='Permanently delete your account and all data'
              left={() => <Trash2 size={24} color={theme.colors.outline} />}
              onPress={() => setShowDeleteDialog(true)}
              titleStyle={{ color: theme.colors.error }}
            />
          </Card.Content>
        </Card>

        {/* Legal Links */}
        <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
          <Card.Content>
            <View style={styles.sectionHeader}>
              <AlertTriangle size={24} color={theme.colors.primary} />
              <Text variant='titleMedium' style={styles.sectionTitle}>
                Legal & Policies
              </Text>
            </View>

            <List.Item
              title='Privacy Policy'
              description='Read our privacy policy'
              left={() => <Shield size={24} color={theme.colors.outline} />}
              onPress={openPrivacyPolicy}
            />
            <Divider />
            <List.Item
              title='Terms of Service'
              description='Read our terms of service'
              left={() => <Globe size={24} color={theme.colors.outline} />}
              onPress={openTermsOfService}
            />
          </Card.Content>
        </Card>
      </ScrollView>

      {/* Change Password Dialog */}
      <Portal>
        <Dialog
          visible={showPasswordDialog}
          onDismiss={() => setShowPasswordDialog(false)}
        >
          <Dialog.Title>Change Password</Dialog.Title>
          <Dialog.Content>
            <TextInput
              label='Current Password'
              value={currentPassword}
              onChangeText={setCurrentPassword}
              secureTextEntry
              mode='outlined'
              style={styles.dialogInput}
            />
            <TextInput
              label='New Password'
              value={newPassword}
              onChangeText={setNewPassword}
              secureTextEntry
              mode='outlined'
              style={styles.dialogInput}
            />
            <TextInput
              label='Confirm New Password'
              value={confirmPassword}
              onChangeText={setConfirmPassword}
              secureTextEntry
              mode='outlined'
              style={styles.dialogInput}
            />
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setShowPasswordDialog(false)}>Cancel</Button>
            <Button onPress={handleChangePassword}>Change Password</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      {/* Delete Account Dialog */}
      <Portal>
        <Dialog
          visible={showDeleteDialog}
          onDismiss={() => setShowDeleteDialog(false)}
        >
          <Dialog.Title>Delete Account</Dialog.Title>
          <Dialog.Content>
            <Text variant='bodyMedium' style={styles.deleteWarning}>
              This action cannot be undone. All your data, recipes, and
              preferences will be permanently deleted.
            </Text>
            <TextInput
              label='Enter your password to confirm'
              value={deletePassword}
              onChangeText={setDeletePassword}
              secureTextEntry
              mode='outlined'
              style={styles.dialogInput}
            />
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button
              onPress={handleDeleteAccount}
              textColor={theme.colors.error}
            >
              Delete Account
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 32,
  },
  card: {
    marginBottom: 16,
    elevation: 2,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitle: {
    marginLeft: 12,
    fontWeight: '600',
  },
  privacySelectorContainer: {
    flexGrow: 0,
    marginTop: 8,
    marginBottom: 16,
    marginLeft: 16,
  },
  privacySelector: {
    flexDirection: 'row',
    gap: 8,
    paddingRight: 16,
  },
  privacyButton: {
    minWidth: 90,
  },
  dialogInput: {
    marginBottom: 16,
  },
  deleteWarning: {
    color: '#d32f2f',
    marginBottom: 16,
    lineHeight: 20,
  },
});
